import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, ExternalLink, Share2, X } from "lucide-react";
import { ACTIVITY_SHARE_LAYOUT, errorMessage, type ActivityShareFormat, type TripDetail } from "@atlas/shared";
import { Button } from "./Button";
import { fetchTripShareUrl, tripShareImageUrl, updateTrip } from "../api/trips";

// Share a trip (spec 35). Mirrors ShareActivityDialog, with two differences:
// the public link is opt-in and can be switched off again (share_enabled is
// checked on every public read), and there is no map-tiles look (the server
// images carry every day's line). A trip with a Strava activity in it gets the
// image only (Strava API Policy).

type Variant = "canvas" | "transparent" | "light";

const FORMATS: { value: ActivityShareFormat; label: string; hint: string }[] = [
  { value: "story", label: "Story", hint: "1080 x 1920" },
  { value: "feed", label: "Feed", hint: "1080 x 1350" },
  { value: "square", label: "Square", hint: "1080 x 1080" },
  { value: "landscape", label: "Landscape", hint: "1200 x 630" },
];

const VARIANTS: { value: Variant; label: string; hint: string }[] = [
  { value: "canvas", label: "Atlas canvas", hint: "Ready to post on its own" },
  { value: "transparent", label: "Transparent", hint: "White text, for dark photos" },
  { value: "light", label: "Light", hint: "Dark text, for pale photos" },
];

const CHECKER =
  "repeating-conic-gradient(rgba(255,255,255,0.08) 0% 25%, rgba(255,255,255,0.02) 0% 50%) 50% / 20px 20px";
const CHECKER_LIGHT = "repeating-conic-gradient(#E4E9EE 0% 25%, #F6F8FA 0% 50%) 50% / 20px 20px";

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "trip"
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

async function shareOrDownload(blob: Blob, filename: string, text: string, url?: string) {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share(url ? { files: [file], text, url } : { files: [file], text });
      return;
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
    }
  }
  downloadBlob(blob, filename);
}

export function ShareTripDialog({ trip, onClose }: { trip: TripDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [format, setFormat] = React.useState<ActivityShareFormat>("story");
  const [variant, setVariant] = React.useState<Variant>("canvas");
  const [copied, setCopied] = React.useState(false);
  const [busy, setBusy] = React.useState<"idle" | "saving" | "error">("idle");
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  // Optimistic copies of the two share flags, so the toggles respond at once.
  const [shareEnabled, setShareEnabled] = React.useState(trip.shareEnabled);
  const [identity, setIdentity] = React.useState(trip.shareIdentity);
  const linkable = !trip.hasStravaMember;

  const linkQuery = useQuery({
    queryKey: ["trip-share-url", trip.id, identity],
    queryFn: () => fetchTripShareUrl(trip.id, identity),
    staleTime: 5 * 60_000,
  });
  const link = linkQuery.data;

  const flagsMutation = useMutation({
    mutationFn: (input: { shareEnabled?: boolean; shareIdentity?: boolean }) => updateTrip(trip.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trips", trip.id] });
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
    onError: () => {
      setShareEnabled(trip.shareEnabled);
      setIdentity(trip.shareIdentity);
    },
  });

  const imageUrl = link
    ? tripShareImageUrl(link.imageUrl, {
        format,
        style: variant === "canvas" ? "brand" : variant === "light" ? "light" : "transparent",
      })
    : null;

  React.useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
  }, [imageUrl]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const layout = ACTIVITY_SHARE_LAYOUT[format];
  const filename = `atlas-${slugify(trip.name)}-${format}${variant === "transparent" ? "-overlay" : variant === "light" ? "-overlay-light" : ""}.png`;
  const publicUrl = linkable && shareEnabled ? link?.url ?? null : null;

  async function handleSave(mode: "download" | "share") {
    if (!imageUrl) return;
    setBusy("saving");
    try {
      const res = await fetch(imageUrl, { credentials: "include" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      if (mode === "share") await shareOrDownload(blob, filename, trip.name, publicUrl ?? undefined);
      else downloadBlob(blob, filename);
      setBusy("idle");
    } catch {
      setBusy("error");
      setTimeout(() => setBusy("idle"), 2000);
    }
  }

  async function handleCopy() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked: the URL is still visible in the field */
    }
  }

  const canShareFiles = typeof navigator !== "undefined" && !!navigator.share && typeof navigator.canShare === "function";

  const segStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    padding: "8px 12px",
    borderRadius: "var(--radius-md)",
    border: `1px solid ${active ? "var(--border-gold)" : "var(--border-default)"}`,
    background: active ? "var(--accent-soft)" : "var(--surface-sunken)",
    color: "var(--text-primary)",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    flex: 1,
    minWidth: 0,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Share trip"
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth: 860,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Share trip</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
              Images sized for social, and a public page you can switch on and off. Gaps between your recordings show
              as dashed lines and never count towards the distance.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: 0, color: "var(--text-secondary)", cursor: "pointer", padding: 4, flexShrink: 0 }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 320px) minmax(0, 1fr)", gap: 20 }}>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: `${layout.width} / ${layout.height}`,
                maxHeight: 460,
                margin: "0 auto",
                borderRadius: "var(--radius-lg)",
                overflow: "hidden",
                border: "1px solid var(--border-subtle)",
                background: variant === "light" ? CHECKER_LIGHT : variant === "transparent" ? CHECKER : "var(--surface-sunken)",
              }}
            >
              {imageUrl && (
                <img
                  key={imageUrl}
                  src={imageUrl}
                  alt={`${trip.name} share image preview`}
                  crossOrigin="use-credentials"
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setImgError(true)}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: imgLoaded ? 1 : 0,
                    transition: "opacity .2s",
                  }}
                />
              )}
              {!imgLoaded && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    padding: 12,
                    textAlign: "center",
                    color: imgError || linkQuery.isError ? "var(--status-danger)" : "var(--text-muted)",
                  }}
                >
                  {imgError || linkQuery.isError ? "Couldn't render the image" : "Rendering…"}
                </div>
              )}
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
              {layout.width} x {layout.height}
              {variant !== "canvas" ? " · transparent background" : ""}
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Format</div>
              <div style={{ display: "flex", gap: 8 }} className="r-wrap">
                {FORMATS.map((f) => (
                  <button key={f.value} type="button" style={segStyle(format === f.value)} onClick={() => setFormat(f.value)}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{f.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Look</div>
              <div style={{ display: "flex", gap: 8 }} className="r-wrap">
                {VARIANTS.map((v) => (
                  <button key={v.value} type="button" style={segStyle(variant === v.value)} onClick={() => setVariant(v.value)}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{v.label}</span>
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{v.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={identity}
                onChange={(e) => {
                  setIdentity(e.target.checked);
                  flagsMutation.mutate({ shareIdentity: e.target.checked });
                }}
                style={{ marginTop: 3, accentColor: "var(--gold-500)" }}
              />
              <span style={{ fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.45 }}>
                Show my name, avatar and level
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
                  Off by default. Atlas branding always shows.
                </span>
              </span>
            </label>

            {!linkable ? (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
                Trips with Strava activities can&#39;t have a public link. You can still save the image and post it
                yourself.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={shareEnabled}
                    onChange={(e) => {
                      setShareEnabled(e.target.checked);
                      flagsMutation.mutate({ shareEnabled: e.target.checked });
                    }}
                    style={{ marginTop: 3, accentColor: "var(--gold-500)" }}
                  />
                  <span style={{ fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.45 }}>
                    Public page
                    <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
                      Anyone with the link can see the route and what it reached. Switch it off to close the link.
                    </span>
                  </span>
                </label>
                {shareEnabled && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                    <input
                      readOnly
                      value={publicUrl ?? (linkQuery.isError ? "Couldn't create link" : "Creating link…")}
                      onFocus={(e) => e.currentTarget.select()}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: "var(--control-sm)",
                        padding: "0 10px",
                        background: "var(--surface-sunken)",
                        border: "1px solid var(--border-default)",
                        borderRadius: "var(--radius-md)",
                        color: "var(--text-secondary)",
                        fontSize: 12.5,
                        fontFamily: "var(--font-mono)",
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={copied ? <Check size={13} /> : <Copy size={13} />}
                      disabled={!publicUrl}
                      onClick={() => void handleCopy()}
                    >
                      {copied ? "Copied" : "Copy"}
                    </Button>
                    {link?.pageUrl && (
                      <a
                        href={link.pageUrl}
                        target="_blank"
                        rel="noreferrer"
                        title="Open the public page"
                        style={{ display: "inline-flex", color: "var(--text-secondary)" }}
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {flagsMutation.isError && (
              <p style={{ margin: 0, fontSize: 12, color: "var(--status-danger)" }}>
                {errorMessage(flagsMutation.error, "Couldn't save that. Try again.")}
              </p>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: "auto" }}>
              <Button
                leftIcon={<Download size={14} />}
                disabled={!imgLoaded || busy === "saving"}
                onClick={() => void handleSave("download")}
              >
                {busy === "saving" ? "Saving…" : busy === "error" ? "Couldn't save" : "Download image"}
              </Button>
              {canShareFiles && (
                <Button
                  variant="secondary"
                  leftIcon={<Share2 size={14} />}
                  disabled={!imgLoaded || busy === "saving"}
                  onClick={() => void handleSave("share")}
                >
                  Share…
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
